import { FormOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import AnnotationsService from '@/domain/Annotations/AnnotationsService.tsx';
import AnnotationForm from './AnnotationForm';
import { Modal } from '@hydrodigital/design-system';

const annotationService = new AnnotationsService();

interface NotesForParamsCardProps {
	openModal: boolean;
	setOpenModal: (value: boolean) => void;
	paramList: string[];
	startDate: string | number | Date | Dayjs;
	endDate: string | number | Date | Dayjs;
	onSuccess: () => void;
	onCancel?: () => void;
}

export default function NotesForParamsModal({
	openModal,
	setOpenModal,
	paramList,
	startDate,
	endDate,
	onSuccess,
	onCancel,
}: NotesForParamsCardProps) {
	const [annotationData, setAnnotationData] = useState({ title: '', comment: '' });
	const [loading, setLoading] = useState(false);

	async function handleSaveAnnotation() {
		setLoading(true);
		try {
			// ✅ Calculate UTC offset to adjust dates before sending to API
			const offsetInMs = dayjs().utcOffset() * 60 * 1000;

			const data = {
				parameterId: paramList,
				// ✅ Subtract offset because dates from graph already have offset applied
				startDate: dayjs(startDate)
					.subtract(offsetInMs, 'milliseconds')
					.format('YYYY-MM-DD HH:mm:ss'),
				endDate: dayjs(endDate).subtract(offsetInMs, 'milliseconds').format('YYYY-MM-DD HH:mm:ss'),
				quality: null,
				comment: annotationData.comment,
				title: annotationData.title,
			};

			const res = await annotationService.postAnnotations(data);

			if (res) {
				notification.success({
					message: 'Successful',
					description: 'Annotation created successfully',
				});
				setAnnotationData({ title: '', comment: '' });
				onSuccess();
			}
		} finally {
			setLoading(false);
		}
	}

	return (
		<Modal isOpen={openModal} onClose={() => setOpenModal(false)} draggable={false} size="m">
			<Modal.Header>
				<Modal.Title text="ADD DATA ANNOTATIONS" />
			</Modal.Header>

			<Modal.Content>
				<AnnotationForm
					title={annotationData.title}
					comment={annotationData.comment}
					onTitleChange={(title) => {
						setAnnotationData((prev) => {
							return { ...prev, title };
						});
					}}
					onCommentChange={(comment) => {
						setAnnotationData((prev) => {
							return { ...prev, comment };
						});
					}}
					onSave={handleSaveAnnotation}
					onCancel={() => {
						setOpenModal(false);
						onCancel?.();
					}}
					loading={loading}
					disabled={!annotationData.title.trim() || !annotationData.comment.trim()}
				/>
			</Modal.Content>
		</Modal>
	);
}
